import React, { useEffect, useState } from "react";
import ModalCloseBtn from "@/components/ModalCloseBtn";
import ReactDOM from "react-dom";
import { Users, Shield, Trash2, KeyRound, Mail, Activity, Settings, Plus, Sparkles, Server, AlertTriangle, Calendar, BookOpen, Briefcase, DollarSign, TrendingUp, Eye, X, Globe, Database, FileText, QrCode, RefreshCw, Search, MapPin, Edit3, Loader2, Download, FileArchive, Upload, UserCheck, CheckCircle2, RotateCcw } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import Layout, { PageHeader, StatCard } from "@/components/Layout";
import api, { BACKEND_URL } from "@/lib/api";
import { useAuth, ROLE_LABELS, ROLE_COLORS } from "@/lib/auth.jsx";
import { getNav } from "@/pages/navConfigs";
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, LineChart, Line, AreaChart, Area } from "recharts";
import EmailTemplatesAdmin from "@/pages/admin/EmailTemplatesAdmin";
import SamplesAdmin from "@/pages/admin/SamplesAdmin";
import { BrandingDefaultsPanel, CustomRolesPanel, EditUserModal } from "@/pages/admin/AdminExtensions";
import { ReassignEventModal, DeleteEventModal, EditEventModal } from "@/components/EventActionsModal";
import SmtpModal from "@/components/admin/SmtpModal";
import { useSubmitting } from "@/lib/useSubmitting";
import RoleBadge from "@/components/RoleBadge";
import PaymentGatewaysPanel from "@/pages/admin/PaymentGatewaysPanel";

const ROLES = ["regional_pm", "project_manager", "social_media_manager", "social_media_executive", "head_media_comm", "media_comm_executive", "head_academics", "seo", "school_principal", "designer", "accountant"];

// Reports-to hierarchy. Managers / Heads roll up to Admin; Executives roll up
// to their team Manager. Custom roles use meeting_scope ("manager" → Admin,
// "executive" → Admin by default, can be overridden manually after creation).
const MANAGER_ROLES = ["regional_pm", "social_media_manager", "head_media_comm", "head_academics", "seo"];
const EXECUTIVE_PARENT_ROLE = {
  project_manager: "regional_pm",
  social_media_executive: "social_media_manager",
  media_comm_executive: "head_media_comm",
};
const SECTIONS = [
  { key: "overview", label: "Overview", Icon: Activity, color: "orange" },
  { key: "users", label: "Users", Icon: Users, color: "indigo" },
  { key: "reports", label: "Reports", Icon: TrendingUp, color: "fuchsia" },
  { key: "orgchart", label: "Org Chart", Icon: Shield, color: "teal" },
  { key: "events", label: "Events", Icon: Calendar, color: "pink" },
  { key: "completed-events", label: "Completed Events", Icon: CheckCircle2, color: "emerald" },
  { key: "regional", label: "Regional PMs", Icon: MapPin, color: "teal" },
  { key: "social", label: "Social Calendar", Icon: Sparkles, color: "pink" },
  { key: "content", label: "Blogs", Icon: BookOpen, color: "violet" },
  { key: "samples", label: "Samples & Keywords", Icon: FileText, color: "orange" },
  { key: "branding", label: "Branding", Icon: Sparkles, color: "fuchsia" },
  { key: "roles", label: "Custom Roles", Icon: Shield, color: "indigo" },
  { key: "emails", label: "Email Triggers", Icon: Mail, color: "orange" },
  { key: "integrations", label: "API & Integrations", Icon: Globe, color: "indigo" },
  { key: "system", label: "System", Icon: Server, color: "orange" },
];

export default function AdminDashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [section, setSection] = useState("overview");
  const [overview, setOverview] = useState(null);
  const [cities, setCities] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [health, setHealth] = useState(null);
  const [smtp, setSmtp] = useState({ host: "", port: 587, user: "", password: "", from_email: "", from_name: "MUNner's" });
  const [showCreate, setShowCreate] = useState(false);
  const [reassignEvent, setReassignEvent] = useState(null);
  const [deleteEvent, setDeleteEvent] = useState(null);
  const [editEvent, setEditEvent] = useState(null);
  const [showCreateEvent, setShowCreateEvent] = useState(false);
  const [createEventForm, setCreateEventForm] = useState({
    name: "", address: "", email: "", school_name: "", school_email: "",
    start_date: "", end_date: "", expected_delegates: 200, delegate_fee: 2500, pm_id: "",
  });
  const eventSubmit = useSubmitting();
  const [showSmtp, setShowSmtp] = useState(false);
  const [eventDossier, setEventDossier] = useState(null);
  const [userSearch, setUserSearch] = useState("");
  const [refreshConfirm, setRefreshConfirm] = useState("");
  const [createForm, setCreateForm] = useState({ email: "", name: "", role: "project_manager", city: "", region: "", parent_id: "", member_id: "", end_tenure_date: "" });
  const [editUser, setEditUser] = useState(null);
  const [customRoles, setCustomRoles] = useState([]);
  const userSubmit = useSubmitting();

  const seedDemo = async () => {
    if (!window.confirm("Create the demo dataset (PM, RPM, SM Mgr, SM Exec, Head M&C, M&C Exec, Head Academics, Principal)?\n\nExisting users with the same email will be skipped.")) return;
    try {
      const r = await api.post("/admin/seed-demo-users");
      toast.success(`Demo seed: ${r.data.actions.length} created · total users: ${r.data.user_count}`);
      load();
    } catch (e) { toast.error("Failed: " + (e?.response?.data?.detail || e.message)); }
  };

  const load = async () => {
    const [o, c, i, h, s, cr] = await Promise.all([
      api.get("/admin/overview"),
      api.get("/cities"),
      api.get("/api-integrations"),
      api.get("/admin/system-health"),
      api.get("/admin/smtp"),
      api.get("/admin/custom-roles").catch(() => ({ data: [] })),
    ]);
    setOverview(o.data); setCities(c.data); setIntegrations(i.data); setHealth(h.data);
    setSmtp({ ...s.data, password: "" });
    setCustomRoles(cr.data || []);
  };

  // Admin-only: archive an event into Completed Events / re-open it.
  const toggleCompleted = async (ev, completed) => {
    const msg = completed
      ? `Mark "${ev.name}" as COMPLETED?\n\nIt disappears from the main dashboard (and the PM's dashboard) and moves to Completed Events. Everything inside stays fully editable. You can re-open it anytime.`
      : `Re-open "${ev.name}"? It moves back to the active events list.`;
    if (!window.confirm(msg)) return;
    try {
      await api.post(`/events/${ev.event_id}/toggle-completed`, { completed });
      toast.success(completed ? `"${ev.name}" marked completed` : `"${ev.name}" re-opened`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to update event status"); }
  };

  useEffect(() => { load(); }, []);

  const createEvent = (e) => {
    e.preventDefault();
    if (!createEventForm.pm_id) { toast.error("Please select a Project Manager — events must have an owner"); return; }
    return eventSubmit.run(async () => {
      try {
        const r = await api.post("/events", {
          ...createEventForm,
          expected_delegates: parseInt(createEventForm.expected_delegates) || 200,
          delegate_fee: parseFloat(createEventForm.delegate_fee) || 2500,
        });
        toast.success(`Event "${r.data.name}" created`);
        setShowCreateEvent(false);
        setCreateEventForm({ name: "", address: "", email: "", school_name: "", school_email: "", start_date: "", end_date: "", expected_delegates: 200, delegate_fee: 2500, pm_id: "" });
        load();
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Could not create event");
      }
    });
  };

  const createUser = (e) => {
    e.preventDefault();
    return userSubmit.run(async () => {
      try {
        const r = await api.post("/users", createForm);
        toast.success(`User created. ${r.data.email_sent ? "Email sent." : `Temp password: ${r.data.temp_password}`}`);
        setShowCreate(false);
        setCreateForm({ email: "", name: "", role: "project_manager", city: "", region: "", parent_id: "", member_id: "", end_tenure_date: "" });
        load();
      } catch (e2) {
        toast.error(e2?.response?.data?.detail || "Failed");
      }
    });
  };

  const resetPwd = async (uid) => {
    if (!window.confirm("Reset password for this user?")) return;
    const r = await api.post(`/users/${uid}/reset-password`);
    toast.success(r.data.email_sent ? "Reset email sent" : `Temp password: ${r.data.temp_password}`);
  };

  const removeUser = async (uid) => {
    if (!window.confirm("Delete this user permanently?")) return;
    await api.delete(`/users/${uid}`);
    toast.success("User deleted");
    load();
  };

  // SMTP save/test/diagnose handled inside <SmtpModal />

  const refreshSystem = async () => {
    if (refreshConfirm !== "DELETE ALL") return toast.error("Type DELETE ALL to confirm");
    const r = await api.post("/admin/refresh-system", { confirm: "DELETE_ALL" });
    toast.success(`Wiped. ${r.data.admins_preserved} admin(s) preserved.`);
    setRefreshConfirm("");
    load();
  };

  const openEvent = async (eid) => {
    const r = await api.get(`/admin/events/${eid}/full`);
    setEventDossier(r.data);
  };

  if (!overview) return <Layout navItems={getNav("admin")}><div className="p-12 text-slate-400">Loading…</div></Layout>;

  const filteredUsers = overview.users.filter((u) => !userSearch || u.name?.toLowerCase().includes(userSearch.toLowerCase()) || u.email?.toLowerCase().includes(userSearch.toLowerCase()));
  const rpms = overview.users.filter((u) => u.role === "regional_pm");
  const admins = overview.users.filter((u) => u.role === "admin");
  // Build full role options: built-in + custom, deduped.
  const allRoleOptions = [
    ...ROLES.map((r) => ({ key: r, label: ROLE_LABELS[r] || r })),
    ...customRoles.filter((cr) => !ROLES.includes(cr.role_key)).map((cr) => ({ key: cr.role_key, label: cr.label, custom: true, meeting_scope: cr.meeting_scope })),
  ];

  // For a given role, return the list of users this person can report to.
  const parentCandidatesFor = (role) => {
    if (!role || role === "school_principal" || role === "admin") return [];
    // Built-in mapping wins.
    if (MANAGER_ROLES.includes(role)) return admins;
    if (EXECUTIVE_PARENT_ROLE[role]) {
      const mgrRole = EXECUTIVE_PARENT_ROLE[role];
      return overview.users.filter((u) => u.role === mgrRole);
    }
    // Custom role — use meeting_scope.
    const cr = customRoles.find((x) => x.role_key === role);
    if (cr) {
      // Managers report to Admin. Executives also default to Admin
      // (the operator can re-assign parent later via Org Chart).
      return admins;
    }
    return [];
  };

  const currentParentCandidates = parentCandidatesFor(createForm.role);
  // Pie palette aligned with ROLES order: rpm, pm, sm_mgr, sm_exec, head_mc, mc_exec, head_academics, principal
  const roleColors = ["#14B8A6", "#F97316", "#EC4899", "#F59E0B", "#EAB308", "#8B5CF6", "#06B6D4", "#A855F7", "#3B82F6"];
  // Colour palette for custom roles in the donut chart. Cycled by index when
  // there are more custom roles than colours.
  const customRoleColors = ["#DB2777", "#0EA5E9", "#D946EF", "#10B981", "#F43F5E", "#7C3AED", "#FB923C", "#22D3EE"];

  return (
    <Layout navItems={getNav("admin")}>
      <div className="p-6 lg:p-10 max-w-7xl mx-auto">
        <PageHeader title={`Welcome, ${user.name.split(" ")[0]} 👑`} subtitle="Platform owner — full visibility & control across every dashboard." icon={Shield} accent="indigo"
          actions={
            <>
              <button onClick={() => setShowSmtp(true)} className="btn-ghost" data-testid="open-smtp-btn"><Mail className="w-4 h-4" /> SMTP</button>
              <button onClick={() => setShowCreate(true)} className="btn-primary" data-testid="open-create-user-btn"><Plus className="w-4 h-4" /> New user</button>
            </>
          } />

        {/* Section tabs */}
        <div className="flex items-center gap-2 overflow-x-auto custom-scrollbar pb-3 mb-6">
          {SECTIONS.map((s) => (
            <button key={s.key} onClick={() => setSection(s.key)} data-testid={`admin-section-${s.key}`}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap transition-all ${section === s.key ? "brand-gradient text-white shadow-lg" : "bg-white text-slate-600 border border-slate-200 hover:border-orange-300"}`}>
              <s.Icon className="w-4 h-4" /> {s.label}
            </button>
          ))}
        </div>

        {/* OVERVIEW */}
        {section === "overview" && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <StatCard label="Users" value={overview.totals.users} icon={Users} color="indigo" trend={`${rpms.length} regional · ${overview.pm_stats.length} PM`} />
              <StatCard label="Events" value={overview.totals.events} icon={Calendar} color="orange" trend={`${overview.totals.committees} committees`} />
              <StatCard label="Allotments" value={overview.totals.allotments} icon={Sparkles} color="pink" trend={`${overview.totals.paid_allotments} paid · ${overview.totals.checked_in} checked in`} />
              <StatCard label="Revenue" value={`₹${(overview.totals.total_revenue || 0).toLocaleString()}`} icon={DollarSign} color="green" trend={`Profit ₹${(overview.totals.total_profit || 0).toLocaleString()}`} />
            </div>

            <KaimeraSummaryBar />

            <div className="grid lg:grid-cols-3 gap-5 mb-8">
              <div className="dashboard-card p-6 lg:col-span-2">
                <h3 className="font-display font-bold text-lg mb-4">Users by Role</h3>
                <div className="grid md:grid-cols-2 gap-4 items-center">
                  {(() => {
                    // Build a unified list of built-in + custom roles with
                    // their colours so the donut chart never has a colourless
                    // bullet (School Principal previously rendered without
                    // a swatch because the palette had 8 colours for 9 roles).
                    const builtIn = ROLES.map((r, i) => ({
                      key: r,
                      label: ROLE_LABELS[r] || r,
                      colour: roleColors[i % roleColors.length],
                      count: overview.users.filter((u) => u.role === r).length,
                    }));
                    const customs = (customRoles || []).map((cr, i) => ({
                      key: cr.role_key,
                      label: `✦ ${cr.name || cr.label || cr.role_key}`,
                      colour: cr.color || customRoleColors[i % customRoleColors.length],
                      count: overview.users.filter((u) => u.role === cr.role_key || (u.custom_roles || []).includes(cr.role_key)).length,
                    }));
                    const all = [...builtIn, ...customs];
                    return (
                      <>
                        <ResponsiveContainer width="100%" height={220}>
                          <PieChart>
                            <Pie data={all.map((r) => ({ name: r.label, value: r.count }))} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80}>
                              {all.map((r, i) => <Cell key={i} fill={r.colour} />)}
                            </Pie>
                            <Tooltip />
                          </PieChart>
                        </ResponsiveContainer>
                        <div className="space-y-2 max-h-56 overflow-y-auto pr-2">
                          {all.map((r) => (
                            <div key={r.key} className="flex items-center gap-3 text-sm">
                              <div className="w-3 h-3 rounded shrink-0" style={{ background: r.colour }} />
                              <div className="text-slate-700 truncate">{r.label}</div>
                              <div className="ml-auto font-bold">{r.count}</div>
                            </div>
                          ))}
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
              <StorageStatsCard />
            </div>

            <div className="dashboard-card p-6 bg-gradient-to-br from-orange-50 to-amber-50 mb-8">
              <h3 className="font-display font-bold text-lg mb-3">Activity Pulse</h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Pulse label="Posts scheduled" done={overview.totals.posts_done} total={overview.totals.posts} color="pink" />
                <Pulse label="Delegates paid" done={overview.totals.paid_allotments} total={overview.totals.allotments} color="orange" />
                <Pulse label="Tasks completed" done={overview.tasks_count - overview.tasks_pending} total={overview.tasks_count} color="teal" />
                <Pulse label="Blogs published" done={overview.blogs.filter((b) => b.published).length} total={overview.blogs.length} color="violet" />
              </div>
            </div>

            {/* Per-event mini cards (active only — completed live in their own tab) */}
            <h3 className="font-display font-bold text-xl mb-3">All Events ({overview.events.filter((e) => !e.completed).length})</h3>
            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
              {overview.events.filter((e) => !e.completed).slice(0, 9).map((e) => <AdminEventCard key={e.event_id} ev={e} onOpen={() => openEvent(e.event_id)} onEdit={() => setEditEvent(e)} onReassign={() => setReassignEvent(e)} onDelete={() => setDeleteEvent(e)} onToggleComplete={() => toggleCompleted(e, true)} />)}
              {overview.events.filter((e) => !e.completed).length > 9 && (
                <button onClick={() => setSection("events")} className="dashboard-card p-5 flex items-center justify-center text-orange-600 font-semibold">View all {overview.events.filter((e) => !e.completed).length} events →</button>
              )}
            </div>
          </motion.div>
        )}

        {/* USERS */}
        {section === "users" && (
          <div className="dashboard-card p-6">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <h3 className="font-display font-bold text-lg">All Users ({overview.users.length})</h3>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-50 border border-slate-200">
                  <Search className="w-4 h-4 text-slate-400" />
                  <input value={userSearch} onChange={(e) => setUserSearch(e.target.value)} placeholder="Search users…" className="bg-transparent outline-none text-sm" data-testid="user-search" />
                </div>
                <button onClick={seedDemo} className="btn-ghost text-xs" title="One-click create the demo dataset (PM, RPM, SM Mgr etc.)" data-testid="seed-demo-btn"><Sparkles className="w-4 h-4" /> Seed demo</button>
                <button onClick={() => setShowCreate(true)} className="btn-primary" data-testid="open-create-user-btn"><Plus className="w-4 h-4" /> New</button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs uppercase text-slate-500 border-b border-slate-100"><th className="py-2 pr-4">Name</th><th>Email</th><th>Role</th><th>City/Region</th><th>Reports to</th><th></th></tr></thead>
                <tbody>
                  {filteredUsers.map((u) => {
                    const parent = overview.users.find((x) => x.user_id === u.parent_id);
                    return (
                      <tr key={u.user_id} className="border-b border-slate-50" data-testid={`user-row-${u.email}`}>
                        <td className="py-3 pr-4 font-semibold">{u.name}{!u.active && <span className="ml-1 text-[10px] bg-slate-200 px-1.5 rounded text-slate-500">INACTIVE</span>}</td>
                        <td>{u.email}</td>
                        <td><RoleBadge role={u.role} customRoles={customRoles} /></td>
                        <td className="text-slate-600">{u.city || u.region || "—"}</td>
                        <td className="text-slate-600">{parent?.name || "—"}</td>
                        <td className="flex gap-1 justify-end py-2 pr-2">
                          <button onClick={() => setEditUser(u)} className="p-2 rounded-lg hover:bg-blue-50 text-blue-500" title="Edit user" data-testid={`edit-user-${u.email}`}><Edit3 className="w-4 h-4" /></button>
                          <button onClick={() => resetPwd(u.user_id)} className="p-2 rounded-lg hover:bg-orange-50 text-orange-500" title="Reset password" data-testid={`reset-pwd-${u.email}`}><KeyRound className="w-4 h-4" /></button>
                          {u.role !== "admin" && <button onClick={() => removeUser(u.user_id)} className="p-2 rounded-lg hover:bg-rose-50 text-rose-500" data-testid={`delete-user-${u.email}`}><Trash2 className="w-4 h-4" /></button>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* EVENTS */}
        {section === "orgchart" && <OrgChartSection users={overview.users} customRoles={customRoles} onChange={load} />}

        {section === "events" && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display font-bold text-xl">All Events</h3>
              <button onClick={() => setShowCreateEvent(true)} className="btn-primary text-sm" data-testid="admin-create-event-btn">
                <Plus className="w-4 h-4" /> Create event
              </button>
            </div>
            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
              {overview.events.filter((e) => !e.completed).map((e) => <AdminEventCard key={e.event_id} ev={e} onOpen={() => openEvent(e.event_id)} onEdit={() => setEditEvent(e)} onReassign={() => setReassignEvent(e)} onDelete={() => setDeleteEvent(e)} onToggleComplete={() => toggleCompleted(e, true)} />)}
              {overview.events.filter((e) => !e.completed).length === 0 && <div className="col-span-full dashboard-card p-12 text-center text-slate-400">No active events. Click <b>Create event</b> above to add one, or check the <b>Completed Events</b> tab.</div>}
            </div>
          </div>
        )}

        {/* COMPLETED EVENTS */}
        {section === "completed-events" && (
          <div data-testid="admin-completed-events">
            <h3 className="font-display font-bold text-xl mb-1">Completed Events</h3>
            <p className="text-sm text-slate-500 mb-4">Events you've marked as completed — hidden from the main dashboards (Admin + PM), everything inside stays fully editable. Use the reopen button to move one back.</p>
            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
              {overview.events.filter((e) => e.completed).map((e) => <AdminEventCard key={e.event_id} ev={e} onOpen={() => openEvent(e.event_id)} onEdit={() => setEditEvent(e)} onReassign={() => setReassignEvent(e)} onDelete={() => setDeleteEvent(e)} onToggleComplete={() => toggleCompleted(e, false)} />)}
              {overview.events.filter((e) => e.completed).length === 0 && <div className="col-span-full dashboard-card p-12 text-center text-slate-400">No completed events yet. On the <b>Events</b> tab, click the ✓ button on an event card to archive it here.</div>}
            </div>
          </div>
        )}

        {/* REGIONAL PMs */}
        {section === "regional" && (
          <div>
            <h3 className="font-display font-bold text-xl mb-3">Regional Project Managers ({overview.rpm_stats.length})</h3>
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              {overview.rpm_stats.map((r) => (
                <div key={r.user_id} className="dashboard-card p-5">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-teal-400 to-cyan-400 flex items-center justify-center text-white font-display font-black">{r.name.charAt(0)}</div>
                    <div>
                      <div className="font-display font-bold text-lg">{r.name}</div>
                      <div className="text-xs text-slate-500">{r.email} · {r.region || "—"}</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 mt-4">
                    <div className="p-3 rounded-xl bg-teal-50"><div className="text-2xl font-display font-black text-teal-700">{r.pms_count}</div><div className="text-xs text-teal-700">Project Managers</div></div>
                    <div className="p-3 rounded-xl bg-orange-50"><div className="text-2xl font-display font-black text-orange-700">{r.events_count}</div><div className="text-xs text-orange-700">Events</div></div>
                  </div>
                </div>
              ))}
              {overview.rpm_stats.length === 0 && <div className="col-span-full dashboard-card p-12 text-center text-slate-400">No Regional PMs yet.</div>}
            </div>
            <h4 className="font-display font-bold text-lg mb-3">Project Managers</h4>
            <div className="grid md:grid-cols-3 gap-3">
              {overview.pm_stats.map((p) => {
                const parent = overview.users.find((u) => u.user_id === p.parent_id);
                return (
                  <div key={p.user_id} className="dashboard-card p-4">
                    <div className="font-bold">{p.name}</div>
                    <div className="text-xs text-slate-500">{p.email}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{p.city || "—"}{parent ? ` · under ${parent.name}` : ""}</div>
                    <div className="flex gap-3 mt-2 text-xs">
                      <span className="font-semibold text-orange-600">{p.events_count} events</span>
                      <span className="font-semibold text-pink-600">{p.allotments_count} allotments</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* SOCIAL CALENDAR */}
        {section === "social" && <SocialReadOnly events={overview.events} />}

        {/* BLOGS */}
        {section === "content" && (
          <div>
            <h3 className="font-display font-bold text-xl mb-3">All Content Writer Blogs ({overview.blogs.length})</h3>
            <div className="grid md:grid-cols-2 gap-4">
              {overview.blogs.map((b) => (
                <div key={b.blog_id} className="dashboard-card p-5">
                  <div className="font-display font-bold text-lg">{b.title}</div>
                  <div className="text-xs text-slate-500 mb-3">{b.published ? <span className="text-emerald-600">Published</span> : <span className="text-amber-600">Draft</span>} · Updated {b.updated_at?.slice(0, 10)}</div>
                  <div className="text-sm text-slate-700 line-clamp-3" dangerouslySetInnerHTML={{ __html: b.content_html?.slice(0, 280) || "" }} />
                  <div className="flex gap-2 mt-3">
                    <button onClick={() => window.open(`${BACKEND_URL}/api/blogs/${b.blog_id}/docx?t=${localStorage.getItem("munners_token")}`)} className="btn-ghost text-xs py-1.5">Download DOCX</button>
                  </div>
                </div>
              ))}
              {overview.blogs.length === 0 && <div className="col-span-full dashboard-card p-12 text-center text-slate-400">No blogs yet.</div>}
            </div>
          </div>
        )}

        {/* INTEGRATIONS */}
        {section === "samples" && <SamplesAdmin />}
        {section === "branding" && <BrandingDefaultsPanel />}
        {section === "roles" && <CustomRolesPanel />}
        {section === "reports" && (
          <div className="dashboard-card p-8 text-center">
            <TrendingUp className="w-12 h-12 text-fuchsia-400 mx-auto mb-3" />
            <h3 className="font-display font-black text-xl mb-2">Reports moved to a dedicated page</h3>
            <p className="text-sm text-slate-500 mb-4">For better focus, the activity timeline opens on its own page.</p>
            <button onClick={() => nav("/dashboard/reports")} className="btn-primary" data-testid="goto-reports-btn">Open Reports →</button>
          </div>
        )}
        {section === "emails" && <EmailTemplatesAdmin />}

        {section === "integrations" && (
          <div>
            <h3 className="font-display font-bold text-xl mb-3">API & Integrations</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {integrations.map((i) => (
                <div key={i.key} className="dashboard-card p-5">
                  <div className="flex items-center justify-between mb-2">
                    <div className="w-10 h-10 rounded-xl bg-orange-100 text-orange-600 flex items-center justify-center"><Globe className="w-5 h-5" /></div>
                    <span className={`role-pill ${i.status === "active" ? "bg-emerald-100 text-emerald-700" : i.status === "embedded" ? "bg-indigo-100 text-indigo-700" : "bg-amber-100 text-amber-700"}`}>{i.status}</span>
                  </div>
                  <div className="font-display font-bold text-lg">{i.name}</div>
                  <div className="text-xs text-slate-500 mt-1">{i.description}</div>
                  {i.key === "smtp" && <button onClick={() => setShowSmtp(true)} className="btn-primary mt-3 py-2 text-xs w-full" data-testid="config-smtp-btn">Configure SMTP</button>}
                  {i.key === "openrouter" && <OpenRouterConfig onSaved={load} />}
                </div>
              ))}
              <MongoAtlasCard />
            </div>
            <div className="mt-4">
              <PaymentGatewaysPanel />
            </div>
          </div>
        )}

        {/* SYSTEM */}
        {section === "system" && (
          <>
          <div className="grid lg:grid-cols-2 gap-5">
            <div className="dashboard-card p-6">
              <h3 className="font-display font-bold text-lg mb-4 flex items-center gap-2"><Server className="w-5 h-5 text-emerald-600" /> System Health</h3>
              <div className="space-y-3">
                <Row label="Database" value={health?.database} ok />
                <Row label="SMTP" value={health?.smtp_configured ? "Configured" : "Not configured"} ok={health?.smtp_configured} />
                <Row label="Total Users" value={health?.collections.users} />
                <Row label="Events" value={health?.collections.events} />
                <Row label="Committees" value={health?.collections.committees} />
                <Row label="Allotments" value={health?.collections.allotments} />
                <Row label="Schools" value={health?.collections.schools} />
                <Row label="Blogs" value={health?.collections.blogs} />
              </div>
            </div>
            <div className="dashboard-card p-6 border-rose-200 bg-rose-50/30">
              <h3 className="font-display font-bold text-lg mb-2 flex items-center gap-2 text-rose-700"><AlertTriangle className="w-5 h-5" /> Danger Zone</h3>
              <p className="text-sm text-rose-700/80 mb-4">Wipes ALL data (events, allotments, users) — but <b>all admin accounts are preserved.</b></p>
              <input value={refreshConfirm} onChange={(e) => setRefreshConfirm(e.target.value)} placeholder='Type "DELETE ALL" to confirm' className="input-pill" data-testid="refresh-confirm-input" />
              <button onClick={refreshSystem} className="mt-3 w-full bg-rose-500 text-white rounded-full py-3 font-bold hover:bg-rose-600" data-testid="refresh-system-btn"><RefreshCw className="w-4 h-4 inline mr-2" /> Refresh / Wipe System</button>
              <SystemRepairButton />
            </div>
          </div>
          <div className="mt-5">
            <GoogleWorkspaceCard />
          </div>
          <div className="mt-5">
            <DatabaseBackupCard />
          </div>
          <div className="grid lg:grid-cols-2 gap-5 mt-5">
            <StorageStatsCard />
            <PingsAndResolverCard />
          </div>
          <div className="mt-5">
            <CityManager />
          </div>
          </>
        )}
      </div>

      {/* Event dossier modal */}
      <AnimatePresence>
        {eventDossier && <EventDossierModal data={eventDossier} onClose={() => setEventDossier(null)} onNav={(eid) => { setEventDossier(null); nav(`/dashboard/events/${eid}`); }} />}
      </AnimatePresence>

      {/* Create user modal */}
      {showCreate && (
        <Modal onClose={() => setShowCreate(false)} title="Create User">
          <form onSubmit={createUser} className="space-y-3">
            <input required placeholder="Full name" className="input-pill" value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} data-testid="cu-name" />
            <input required type="email" placeholder="Email" className="input-pill" value={createForm.email} onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} data-testid="cu-email" />
            <select className="input-pill" value={createForm.role} onChange={(e) => {
              const role = e.target.value;
              const COUNTRY_ROLES = ["regional_pm", "social_media_manager", "head_media_comm", "head_academics", "seo"];
              // Clear parent_id when role changes — different role → different reports-to pool.
              const next = { ...createForm, role, parent_id: "" };
              if (COUNTRY_ROLES.includes(role)) next.city = ""; // these cover ALL cities
              // Auto-pick supervisor if there's exactly one eligible candidate.
              const candidates = parentCandidatesFor(role);
              if (candidates.length === 1) next.parent_id = candidates[0].user_id;
              setCreateForm(next);
            }} data-testid="cu-role">
              <optgroup label="Built-in roles">
                {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>)}
              </optgroup>
              {allRoleOptions.some((o) => o.custom) && (
                <optgroup label="Custom roles">
                  {allRoleOptions.filter((o) => o.custom).map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
                </optgroup>
              )}
            </select>
            {!["regional_pm", "social_media_manager", "head_media_comm", "head_academics", "seo"].includes(createForm.role) && (
              <>
                <input list="munners-cities" required placeholder="City (type or pick)" className="input-pill" value={createForm.city} onChange={(e) => setCreateForm({ ...createForm, city: e.target.value })} data-testid="cu-city" />
                <datalist id="munners-cities">
                  {cities.map((c) => <option key={c} value={c} />)}
                </datalist>
              </>
            )}
            <input list="munners-countries" required placeholder="Region (Country — e.g. India)" className="input-pill" value={createForm.region} onChange={(e) => setCreateForm({ ...createForm, region: e.target.value })} data-testid="cu-region" />
            <datalist id="munners-countries">
              {["India", "United States", "United Kingdom", "United Arab Emirates", "Canada", "Australia", "Singapore", "South Africa", "Germany", "France", "Netherlands", "Saudi Arabia", "Qatar", "Kuwait", "Bahrain", "Oman", "Sri Lanka", "Nepal", "Bangladesh", "Bhutan", "Maldives", "Indonesia", "Malaysia", "Thailand", "Philippines", "Vietnam", "Japan", "South Korea", "China", "Brazil", "Mexico", "Argentina", "Spain", "Italy", "Portugal", "Switzerland", "Sweden", "Norway", "Denmark", "Finland", "Belgium", "Ireland", "New Zealand", "Egypt", "Kenya", "Nigeria", "Turkey", "Israel"].map((c) => <option key={c} value={c} />)}
            </datalist>
            {currentParentCandidates.length > 0 && createForm.role !== "school_principal" && (
              <div>
                <label className="text-[10px] uppercase font-bold text-slate-500 block mb-1">
                  Reports to · {MANAGER_ROLES.includes(createForm.role) || customRoles.some((c) => c.role_key === createForm.role && c.meeting_scope === "manager") ? "Admin" : (ROLE_LABELS[EXECUTIVE_PARENT_ROLE[createForm.role]] || "Manager")}
                </label>
                <select
                  className="input-pill"
                  value={createForm.parent_id}
                  onChange={(e) => setCreateForm({ ...createForm, parent_id: e.target.value })}
                  required={MANAGER_ROLES.includes(createForm.role) || !!EXECUTIVE_PARENT_ROLE[createForm.role]}
                  data-testid="cu-parent"
                >
                  <option value="">Select supervisor…</option>
                  {currentParentCandidates.map((r) => <option key={r.user_id} value={r.user_id}>{r.name} · {ROLE_LABELS[r.role] || r.role}</option>)}
                </select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] uppercase font-bold text-slate-500 block mb-1">Member ID</label>
                <input placeholder="e.g. MN-2026-001" className="input-pill" value={createForm.member_id} onChange={(e) => setCreateForm({ ...createForm, member_id: e.target.value })} data-testid="cu-member-id" />
              </div>
              <div>
                <label className="text-[10px] uppercase font-bold text-slate-500 block mb-1">End of Tenure</label>
                <input type="date" className="input-pill" value={createForm.end_tenure_date} onChange={(e) => setCreateForm({ ...createForm, end_tenure_date: e.target.value })} data-testid="cu-end-tenure" />
              </div>
            </div>
            <button type="submit" disabled={userSubmit.submitting} className="btn-primary w-full py-3 disabled:opacity-60 inline-flex items-center justify-center gap-2" data-testid="cu-submit">
              {userSubmit.submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Creating & emailing…</> : "Create & email password"}
            </button>
          </form>
        </Modal>
      )}

      {/* Admin: create event modal */}
      {showCreateEvent && (
        <Modal onClose={() => setShowCreateEvent(false)} title="Create Event (Admin)">
          <p className="text-xs text-slate-500 mb-3">Use this when a Regional PM isn&apos;t available. You must assign a Project Manager — the event will land in their dashboard. The PM&apos;s Regional Manager (if any) is auto-detected.</p>
          <form onSubmit={createEvent} className="space-y-3">
            <input required placeholder="Event name" className="input-pill" value={createEventForm.name} onChange={(e) => setCreateEventForm({ ...createEventForm, name: e.target.value })} data-testid="admin-ev-name" />
            <input required placeholder="Address" className="input-pill" value={createEventForm.address} onChange={(e) => setCreateEventForm({ ...createEventForm, address: e.target.value })} data-testid="admin-ev-address" />
            <input required type="email" placeholder="Event email" className="input-pill" value={createEventForm.email} onChange={(e) => setCreateEventForm({ ...createEventForm, email: e.target.value })} data-testid="admin-ev-email" />
            <input required placeholder="Host school name" className="input-pill" value={createEventForm.school_name} onChange={(e) => setCreateEventForm({ ...createEventForm, school_name: e.target.value })} data-testid="admin-ev-school" />
            <input type="email" placeholder="School email (optional)" className="input-pill" value={createEventForm.school_email} onChange={(e) => setCreateEventForm({ ...createEventForm, school_email: e.target.value })} data-testid="admin-ev-school-email" />
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] uppercase font-bold text-slate-500 block mb-1">Start date</label>
                <input required type="date" className="input-pill" value={createEventForm.start_date} onChange={(e) => setCreateEventForm({ ...createEventForm, start_date: e.target.value })} data-testid="admin-ev-start" />
              </div>
              <div>
                <label className="text-[10px] uppercase font-bold text-slate-500 block mb-1">End date</label>
                <input required type="date" className="input-pill" value={createEventForm.end_date} onChange={(e) => setCreateEventForm({ ...createEventForm, end_date: e.target.value })} data-testid="admin-ev-end" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] uppercase font-bold text-slate-500 block mb-1">Expected delegates</label>
                <input type="number" min={1} className="input-pill" value={createEventForm.expected_delegates} onChange={(e) => setCreateEventForm({ ...createEventForm, expected_delegates: e.target.value })} data-testid="admin-ev-delegates" />
              </div>
              <div>
                <label className="text-[10px] uppercase font-bold text-slate-500 block mb-1">Delegate fee (₹)</label>
                <input type="number" min={0} className="input-pill" value={createEventForm.delegate_fee} onChange={(e) => setCreateEventForm({ ...createEventForm, delegate_fee: e.target.value })} data-testid="admin-ev-fee" />
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase font-bold text-slate-500 block mb-1">Assign to Project Manager *</label>
              <select required className="input-pill" value={createEventForm.pm_id} onChange={(e) => setCreateEventForm({ ...createEventForm, pm_id: e.target.value })} data-testid="admin-ev-pm">
                <option value="">Select a Project Manager…</option>
                {(overview?.users || []).filter((u) => u.role === "project_manager" && u.active).map((p) => (
                  <option key={p.user_id} value={p.user_id}>{p.name} · {p.city || p.region || "—"} · {p.email}</option>
                ))}
              </select>
              {(overview?.users || []).filter((u) => u.role === "project_manager" && u.active).length === 0 && (
                <div className="text-xs text-rose-600 mt-1">No active Project Managers found. Create one in the Users tab first.</div>
              )}
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={() => setShowCreateEvent(false)} className="btn-ghost flex-1 py-3">Cancel</button>
              <button type="submit" disabled={eventSubmit.submitting} className="btn-primary flex-1 py-3 disabled:opacity-60 inline-flex items-center justify-center gap-2" data-testid="admin-ev-submit">
                {eventSubmit.submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Creating…</> : "Create event"}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* SMTP modal */}
      {showSmtp && (
        <SmtpModal initial={smtp} onClose={() => setShowSmtp(false)} onSaved={load} />
      )}

      {editUser && (
        <EditUserModal user={editUser} allUsers={overview.users} customRoles={customRoles} onClose={() => setEditUser(null)} onSaved={() => { setEditUser(null); load(); }} />
      )}

      {reassignEvent && (
        <ReassignEventModal
          event={reassignEvent}
          projectManagers={overview.users.filter((u) => u.role === "project_manager")}
          onClose={() => setReassignEvent(null)}
          onDone={load}
        />
      )}

      {editEvent && (
        <EditEventModal event={editEvent} onClose={() => setEditEvent(null)} onDone={load} />
      )}

      {deleteEvent && (
        <DeleteEventModal event={deleteEvent} onClose={() => setDeleteEvent(null)} onDone={load} />
      )}
    </Layout>
  );
}

function Pulse({ label, done, total, color }) {
  const pct = total ? Math.round((done / total) * 100) : 0;
  const colors = { pink: "bg-pink-500", orange: "bg-orange-500", teal: "bg-teal-500", violet: "bg-violet-500" };
  return (
    <div>
      <div className="flex justify-between text-xs mb-1">
        <span className="font-semibold text-slate-700">{label}</span>
        <span className="text-slate-500">{done} / {total || 0}</span>
      </div>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full ${colors[color]}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function Row({ label, value, ok }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-slate-500">{label}</span>
      <span className={`font-semibold ${ok === true ? "text-emerald-600" : ok === false ? "text-amber-600" : ""}`}>{value}</span>
    </div>
  );
}

function AdminEventCard({ ev, onOpen, onEdit, onReassign, onDelete, onToggleComplete }) {
  return (
    <motion.div whileHover={{ y: -3 }} className={`dashboard-card p-5 ${ev.completed ? "border-l-4 border-emerald-400" : ""}`} data-testid={`admin-event-card-${ev.event_id}`}>
      <div className="flex items-center gap-3 mb-3">
        {ev.logo_url ? <img src={`${BACKEND_URL}${ev.logo_url}`} className="w-12 h-12 rounded-xl object-cover" alt="" /> :
          <div className="w-12 h-12 rounded-xl brand-gradient flex items-center justify-center text-white font-display font-black">{ev.name.charAt(0)}</div>}
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold truncate">{ev.name}{ev.completed && <span className="ml-2 text-[9px] font-bold uppercase bg-emerald-100 text-emerald-700 rounded-full px-1.5 py-0.5 align-middle">Completed</span>}</div>
          <div className="text-xs text-slate-500 truncate">{ev.school_name} · {ev.pm_name || "—"}</div>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 text-xs mb-3">
        <div><div className="text-slate-400">Allots</div><div className="font-bold">{ev.allotments_count}</div></div>
        <div><div className="text-slate-400">Paid</div><div className="font-bold text-emerald-600">{ev.paid_count}</div></div>
        <div><div className="text-slate-400">Profit</div><div className="font-bold brand-gradient-text">₹{(ev.net_profit / 1000).toFixed(0)}k</div></div>
      </div>
      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mb-3">
        <div className="h-full brand-gradient" style={{ width: `${ev.progress}%` }} />
      </div>
      <div className="flex gap-1.5">
        <button onClick={onOpen} className="btn-ghost text-xs py-2 flex-1" data-testid={`admin-view-event-${ev.event_id}`}><Eye className="w-3 h-3" /> Dossier</button>
        {onToggleComplete && (
          <button onClick={onToggleComplete} className={`btn-ghost text-xs py-2 px-2 ${ev.completed ? "text-amber-600 hover:bg-amber-50" : "text-emerald-600 hover:bg-emerald-50"}`} data-testid={`admin-toggle-complete-${ev.event_id}`} title={ev.completed ? "Re-open this event (moves back to active)" : "Mark as completed (moves to Completed Events)"}>
            {ev.completed ? <RotateCcw className="w-3.5 h-3.5" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
          </button>
        )}
        <button onClick={onEdit} className="btn-ghost text-xs py-2 px-2 text-indigo-600 hover:bg-indigo-50" data-testid={`admin-edit-event-${ev.event_id}`} title="Edit event details">
          <Edit3 className="w-3.5 h-3.5" />
        </button>
        <button onClick={onReassign} className="btn-ghost text-xs py-2 px-2 text-orange-600 hover:bg-orange-50" data-testid={`admin-reassign-${ev.event_id}`} title="Reassign to a different PM">
          <UserCheck className="w-3.5 h-3.5" />
        </button>
        <button onClick={onDelete} className="btn-ghost text-xs py-2 px-2 text-rose-600 hover:bg-rose-50" data-testid={`admin-delete-event-${ev.event_id}`} title="Delete event">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </motion.div>
  );
}

function EventDossierModal({ data, onClose, onNav }) {
  const { event, committees, allotments, budget, posts, vendors, collaborations, merchandise, trophies } = data;
  return (
    <div className="fixed inset-0 z-50 bg-black/40 overflow-y-auto">
      <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="min-h-screen p-4 flex items-start justify-center relative">
        <div className="bg-white rounded-2xl max-w-4xl w-full my-8 max-h-[90vh] overflow-y-auto custom-scrollbar">
          <div className="p-6 border-b border-slate-100 flex items-center gap-4 sticky top-0 bg-white z-10 rounded-t-2xl">
            {event.logo_url ? <img src={`${BACKEND_URL}${event.logo_url}`} className="w-14 h-14 rounded-xl object-cover" alt="" /> :
              <div className="w-14 h-14 rounded-xl brand-gradient flex items-center justify-center text-white font-display font-black text-xl">{event.name.charAt(0)}</div>}
            <div className="flex-1">
              <h3 className="font-display font-black text-2xl">{event.name}</h3>
              <div className="text-sm text-slate-500">{event.school_name} · {event.start_date} → {event.end_date}</div>
            </div>
            <button onClick={() => onNav(event.event_id)} className="btn-primary py-2 text-sm">Open full editor →</button>
            <button onClick={onClose}><X className="w-5 h-5 text-slate-400" /></button>
          </div>
          <div className="p-6 space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Box label="Revenue" value={`₹${budget.total_revenue.toLocaleString()}`} />
              <Box label="Expenses" value={`₹${budget.total_expenses.toLocaleString()}`} />
              <Box label="Net Profit" value={`₹${budget.net_profit.toLocaleString()}`} />
              <Box label="Kaimera total" value={`₹${budget.kaimera_total.toLocaleString()}`} />
            </div>

            <Section title={`Committees (${committees.length})`}>
              {committees.map((c) => (
                <div key={c.committee_id} className="p-3 rounded-xl bg-slate-50">
                  <div className="font-bold">{c.name}</div>
                  <div className="text-xs text-slate-500">{c.agenda || "Agenda TBD"}</div>
                  <div className="text-xs mt-1">{(c.matrix || []).length} portfolios · {allotments.filter((a) => a.committee_id === c.committee_id).length} allotted</div>
                </div>
              ))}
            </Section>

            <Section title={`Budget breakdown`}>
              <div className="grid md:grid-cols-2 gap-3">
                <BudgetList title="Incomes" items={budget.incomes} color="text-emerald-700" />
                <BudgetList title="Expenses" items={budget.expenses} color="text-rose-700" />
              </div>
            </Section>

            <Section title={`Vendors (${vendors.length}), Collaborations (${collaborations.length}), Trophies (${trophies.length}), Merchandise (${merchandise.length})`}>
              <div className="grid md:grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="font-bold mb-1">Collaborations</div>
                  {collaborations.map((c) => <div key={c.collaboration_id} className="text-xs text-slate-600">{c.name} — {c.providing}</div>)}
                </div>
                <div>
                  <div className="font-bold mb-1">Trophies</div>
                  {trophies.map((t) => <div key={t.trophy_id} className="text-xs text-slate-600">{t.award} · ₹{t.cash_prize}</div>)}
                </div>
              </div>
            </Section>

            <Section title={`Social calendar — ${posts.length} posts, ${posts.filter((p) => p.posted).length} done`}>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
                {posts.slice(0, 12).map((p) => (
                  <div key={p.post_id} className="p-2 rounded-lg bg-slate-50">
                    <div className="font-bold">{p.scheduled_date}</div>
                    <div className="truncate">{p.title}</div>
                    <div className={p.posted ? "text-emerald-600" : "text-amber-600"}>{p.posted ? "Posted" : "Pending"}</div>
                  </div>
                ))}
              </div>
            </Section>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

function Box({ label, value }) {
  return <div className="p-3 rounded-xl bg-orange-50"><div className="text-xs text-orange-700 font-bold uppercase">{label}</div><div className="text-lg font-display font-black brand-gradient-text">{value}</div></div>;
}

function KaimeraSummaryBar() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/admin/kaimera-summary").then((r) => setData(r.data)).catch(() => setData(null));
  }, []);
  if (!data) return null;
  return (
    <div className="dashboard-card p-5 mb-8 bg-gradient-to-r from-orange-50 via-white to-emerald-50" data-testid="kaimera-summary-bar">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase font-bold text-slate-500 tracking-wider">Kaimera P&L across all events</div>
          <div className="mt-1 flex items-baseline gap-3 flex-wrap">
            <div className="font-display font-black text-3xl brand-gradient-text">₹{(data.net_profit || 0).toLocaleString()}</div>
            <div className="text-xs text-slate-500">
              Income <b className="text-emerald-700">₹{(data.total_income || 0).toLocaleString()}</b> · Expenses <b className="text-rose-700">₹{(data.total_expenses || 0).toLocaleString()}</b>
            </div>
          </div>
        </div>
        <div className="text-xs text-slate-500 max-w-[420px]">Auto-computed from every event's Kaimera fee tiers + Admin-recorded expenses. Open any event → <b>Kaimera P&L</b> tab to adjust.</div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h4 className="font-display font-bold text-lg mb-3">{title}</h4>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function BudgetList({ title, items, color }) {
  return (
    <div>
      <div className={`font-bold mb-2 ${color}`}>{title}</div>
      <div className="space-y-1 text-sm max-h-48 overflow-y-auto custom-scrollbar">
        {items.length === 0 && <div className="text-slate-400 text-xs">None</div>}
        {items.map((i) => (
          <div key={i.item_id} className="flex justify-between text-xs">
            <span className="text-slate-600">{i.label} × {i.quantity}</span>
            <span className="font-semibold">₹{(i.amount * i.quantity).toLocaleString()}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function SocialReadOnly({ events }) {
  const [selectedEventId, setSelectedEventId] = useState(events[0]?.event_id || "");
  const [posts, setPosts] = useState([]);
  useEffect(() => {
    if (!selectedEventId) return;
    api.get(`/events/${selectedEventId}/social-posts`).then((r) => setPosts(r.data)).catch(() => setPosts([]));
  }, [selectedEventId]);
  const grouped = posts.reduce((acc, p) => { (acc[p.scheduled_date] = acc[p.scheduled_date] || []).push(p); return acc; }, {});
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display font-bold text-xl">Social Calendar (read-only)</h3>
        <select className="input-pill py-2 max-w-xs" value={selectedEventId} onChange={(e) => setSelectedEventId(e.target.value)} data-testid="admin-sm-event-select">
          {events.map((e) => <option key={e.event_id} value={e.event_id}>{e.name}</option>)}
        </select>
      </div>
      {Object.keys(grouped).length === 0 ? (
        <div className="dashboard-card p-12 text-center text-slate-400">No posts scheduled for this event.</div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {Object.entries(grouped).sort().map(([date, items]) => (
            <div key={date} className="dashboard-card p-4">
              <div className="font-display font-bold text-pink-600 mb-2">{date}</div>
              {items.map((p) => (
                <div key={p.post_id} className="text-sm py-1 border-t border-slate-50 first:border-0">
                  <div className="font-semibold">{p.title}</div>
                  <div className="text-xs text-slate-500">{p.platform} · {p.posted ? <span className="text-emerald-600">posted</span> : <span className="text-amber-600">pending</span>}</div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Modal({ children, onClose, title }) {
  // Render to document.body via portal so CSS from parent cards (hover, transforms,
  // shadows) can't affect the modal's positioning or trigger flicker on mousemove.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] bg-black/40 flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="modal-backdrop"
    >
      <div
        className="bg-white rounded-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto shadow-2xl"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="font-display font-black text-2xl mb-4">{title}</h3>
        {children}
      </div>
    </div>,
    document.body
  );
}

function OpenRouterConfig({ onSaved }) {
  const [cfg, setCfg] = useState({ api_key: "", model: "", available_models: [], configured: false });
  const [open, setOpen] = useState(false);
  useEffect(() => { api.get("/ai/config").then((r) => setCfg({ ...r.data, api_key: "" })); }, []);
  const save = async () => {
    const payload = { model: cfg.model };
    if (cfg.api_key) payload.api_key = cfg.api_key;
    const r = await api.post("/admin/ai/config", payload);
    toast.success("OpenRouter saved");
    setCfg({ ...cfg, ...r.data, api_key: "" });
    setOpen(false);
    onSaved && onSaved();
  };
  return (
    <>
      <button onClick={() => setOpen(true)} className="btn-primary mt-3 py-2 text-xs w-full" data-testid="config-openrouter-btn">{cfg.configured ? "Update OpenRouter" : "Configure OpenRouter"}</button>
      {open && (
        <Modal onClose={() => setOpen(false)} title="OpenRouter Settings">
          <div className="space-y-3">
            <div>
              <label className="text-xs text-slate-500">API key (get free at openrouter.ai)</label>
              <input type="password" placeholder={cfg.configured ? "•••• (leave blank to keep)" : "sk-or-…"} className="input-pill mt-1" value={cfg.api_key} onChange={(e) => setCfg({ ...cfg, api_key: e.target.value })} data-testid="openrouter-key" />
            </div>
            <div>
              <label className="text-xs text-slate-500">Free model</label>
              <select className="input-pill mt-1" value={cfg.model} onChange={(e) => setCfg({ ...cfg, model: e.target.value })} data-testid="openrouter-model">
                {cfg.available_models.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </div>
            <button onClick={save} className="btn-primary w-full py-3" data-testid="openrouter-save">Save</button>
          </div>
        </Modal>
      )}
    </>
  );
}

function MongoAtlasCard() {
  const [cfg, setCfg] = useState(null);
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("parts"); // 'parts' or 'uri'
  const [form, setForm] = useState({ cluster_url: "", username: "", password: "", db_name: "", mongo_url: "" });
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { const r = await api.get("/admin/db-config"); setCfg(r.data); setForm((f) => ({ ...f, db_name: r.data.db_name })); }
    catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const buildPayload = () => {
    if (mode === "uri") return { mongo_url: form.mongo_url, db_name: form.db_name };
    return { cluster_url: form.cluster_url, username: form.username, password: form.password, db_name: form.db_name };
  };

  const runTest = async () => {
    setTesting(true); setTestResult(null);
    try { const r = await api.post("/admin/db-config/test", buildPayload()); setTestResult(r.data); }
    catch (e) { setTestResult({ ok: false, message: e?.response?.data?.detail || "Failed" }); }
    finally { setTesting(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/admin/db-config", buildPayload());
      const seedMsg = (r.data.seed_actions || []).length
        ? `· seeded ${r.data.seed_actions.length} user/index entries`
        : "";
      toast.success(`Connected to ${r.data.db_name} — live switch complete ${seedMsg}`);
      setCfg({ ...cfg, source: r.data.source, mongo_url_masked: r.data.mongo_url_masked, db_name: r.data.db_name });
      setOpen(false);
      setForm({ cluster_url: "", username: "", password: "", db_name: r.data.db_name, mongo_url: "" });
      setTestResult(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const reseed = async () => {
    if (!window.confirm("Re-run admin + indexes seed into the current database?\n\nOnly the platform admin is guaranteed — no demo users will be created. Use the 'Seed demo' button for that.")) return;
    try {
      const r = await api.post("/admin/db-config/seed");
      toast.success(`Seed complete · ${r.data.user_count} users in DB · ${r.data.actions.length} actions`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Seed failed");
    }
  };

  const revertToEnv = async () => {
    if (!window.confirm("Revert to the environment-configured database? The app will switch back immediately.")) return;
    try {
      const r = await api.delete("/admin/db-config");
      toast.success("Reverted to environment database");
      setCfg({ ...cfg, source: r.data.source, mongo_url_masked: r.data.mongo_url_masked, db_name: r.data.db_name });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (!cfg) return null;
  const isOverride = cfg.source === "override";

  return (
    <div className="dashboard-card p-5" data-testid="mongo-atlas-card">
      <div className="flex items-center justify-between mb-2">
        <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-600 flex items-center justify-center">
          <Database className="w-5 h-5" />
        </div>
        <span className={`role-pill ${isOverride ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
          {isOverride ? "Admin override" : "From .env"}
        </span>
      </div>
      <div className="font-display font-bold text-lg">MongoDB Atlas</div>
      <div className="text-xs text-slate-500 mt-1">Override the database connection at runtime. Live switchover — no restart needed.</div>
      <div className="mt-3 text-xs space-y-1 bg-slate-50 p-2 rounded-lg">
        <div><span className="text-slate-400">URI:</span> <span className="font-mono break-all" data-testid="mongo-current-uri">{cfg.mongo_url_masked}</span></div>
        <div><span className="text-slate-400">DB:</span> <span className="font-semibold" data-testid="mongo-current-db">{cfg.db_name}</span></div>
      </div>
      <div className="flex gap-2 mt-3">
        <button onClick={() => setOpen(true)} className="btn-primary py-2 text-xs flex-1" data-testid="config-mongo-btn">
          {isOverride ? "Update Atlas" : "Configure Atlas"}
        </button>
        {isOverride && (
          <button onClick={revertToEnv} className="btn-ghost py-2 text-xs" data-testid="mongo-revert-btn">Revert</button>
        )}
      </div>
      <button onClick={reseed} className="mt-2 w-full text-[11px] text-emerald-700 hover:underline" data-testid="mongo-reseed-btn">
        Re-seed admin + demo users into current DB
      </button>
      {open && (
        <Modal onClose={() => setOpen(false)} title="MongoDB Atlas Connection">
          <div className="space-y-3">
            <div className="flex gap-2">
              <button onClick={() => setMode("parts")} className={`text-xs px-3 py-1.5 rounded-full ${mode === "parts" ? "brand-gradient text-white" : "bg-slate-100"}`} data-testid="mongo-mode-parts">Cluster + User + Pass</button>
              <button onClick={() => setMode("uri")} className={`text-xs px-3 py-1.5 rounded-full ${mode === "uri" ? "brand-gradient text-white" : "bg-slate-100"}`} data-testid="mongo-mode-uri">Full URI</button>
            </div>
            {mode === "parts" ? (
              <>
                <div>
                  <label className="text-xs text-slate-500">Cluster URL</label>
                  <input className="input-pill mt-1" placeholder="cluster0.abcde.mongodb.net" value={form.cluster_url} onChange={(e) => setForm({ ...form, cluster_url: e.target.value })} data-testid="mongo-cluster" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs text-slate-500">Username</label>
                    <input className="input-pill mt-1" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} data-testid="mongo-username" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">Password</label>
                    <input type="password" className="input-pill mt-1" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="mongo-password" />
                  </div>
                </div>
              </>
            ) : (
              <div>
                <label className="text-xs text-slate-500">MongoDB URI</label>
                <input className="input-pill mt-1" placeholder="mongodb+srv://user:pass@cluster.../?retryWrites=true" value={form.mongo_url} onChange={(e) => setForm({ ...form, mongo_url: e.target.value })} data-testid="mongo-uri" />
              </div>
            )}
            <div>
              <label className="text-xs text-slate-500">Database name</label>
              <input className="input-pill mt-1" placeholder="munners_prod" value={form.db_name} onChange={(e) => setForm({ ...form, db_name: e.target.value })} data-testid="mongo-dbname" />
            </div>
            {testResult && (
              <div className={`p-3 rounded-lg text-xs ${testResult.ok ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`} data-testid="mongo-test-result">
                <div className="font-bold">{testResult.ok ? "Connection OK" : "Connection failed"}</div>
                <div className="break-words">{testResult.message}</div>
              </div>
            )}
            <div className="flex gap-2">
              <button onClick={runTest} disabled={testing} className="btn-ghost flex-1 py-3" data-testid="mongo-test-btn">{testing ? "Testing…" : "Test connection"}</button>
              <button onClick={save} disabled={saving || (testResult && !testResult.ok)} className="btn-primary flex-1 py-3 disabled:opacity-50" data-testid="mongo-save-btn">{saving ? "Switching…" : "Save & Switch"}</button>
            </div>
            <p className="text-[11px] text-slate-400">Tip: run <b>Test connection</b> first. <b>Save & Switch</b> swaps the live database instantly — existing requests in-flight will use the new connection.</p>
          </div>
        </Modal>
      )}
    </div>
  );
}


function OrgChartSection({ users, customRoles = [], onChange }) {
  const [draggingId, setDraggingId] = useState(null);
  const [hoverParentId, setHoverParentId] = useState(null);
  const [cityFilter, setCityFilter] = useState("all");

  const allCities = Array.from(new Set(users.map((u) => u.city).filter(Boolean))).sort();
  const allRegions = Array.from(new Set(users.map((u) => u.region).filter(Boolean))).sort();

  // Filter logic: when a city is selected, show users with that city + users with no city (regional roles cover all)
  const filteredUsers = cityFilter === "all"
    ? users
    : users.filter((u) => u.city === cityFilter || !u.city);

  // Group by role for column layout
  const columns = [
    { key: "admin", label: "Admin" },
    { key: "regional_pm", label: "Regional PM" },
    { key: "project_manager", label: "Project Manager" },
    { key: "social_media_manager", label: "SM Manager" },
    { key: "social_media_executive", label: "SM Executive" },
    { key: "head_media_comm", label: "Head Media & Comm." },
    { key: "media_comm_executive", label: "M&C Executive" },
    { key: "head_academics", label: "Head Academics & Research" },
    { key: "school_principal", label: "Principal" },
  ];

  const byRole = (r) => filteredUsers.filter((u) => u.role === r);
  const findUser = (uid) => users.find((u) => u.user_id === uid);

  const reassign = async (uid, parentId) => {
    try {
      await api.patch(`/admin/users/${uid}/parent`, { parent_id: parentId || null });
      toast.success("Reporting chain updated");
      onChange && onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div data-testid="org-chart-section">
      <div className="flex items-start justify-between mb-3 gap-4 flex-wrap">
        <div>
          <h3 className="font-display font-bold text-xl">Org Chart — Reporting Chain</h3>
          <p className="text-sm text-slate-500">Filter by city, drag a user card onto another to set their parent, or use the dropdown.</p>
          {allRegions.length > 0 && <p className="text-[11px] text-slate-400 mt-0.5">Regions (countries): {allRegions.join(", ")}</p>}
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs font-semibold text-slate-500">City</label>
          <select value={cityFilter} onChange={(e) => setCityFilter(e.target.value)} className="text-xs px-3 py-1.5 rounded-full bg-white border border-slate-200" data-testid="org-city-filter">
            <option value="all">All cities</option>
            {allCities.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      </div>

      <div className="flex gap-4 overflow-x-auto pb-4">
        {columns.map((col) => {
          const list = byRole(col.key);
          if (list.length === 0) return null;
          return (
            <div key={col.key} className="flex-shrink-0 w-72 dashboard-card p-4">
              <div className="font-display font-bold text-sm mb-3 text-slate-700">{col.label} <span className="text-xs text-slate-400 font-normal">({list.length})</span></div>
              <div className="space-y-2">
                {list.map((u) => {
                  const parent = u.parent_id ? findUser(u.parent_id) : null;
                  const isDropTarget = draggingId && draggingId !== u.user_id && hoverParentId === u.user_id;
                  return (
                    <div
                      key={u.user_id}
                      draggable
                      onDragStart={() => setDraggingId(u.user_id)}
                      onDragEnd={() => { setDraggingId(null); setHoverParentId(null); }}
                      onDragOver={(e) => { e.preventDefault(); setHoverParentId(u.user_id); }}
                      onDragLeave={() => setHoverParentId(null)}
                      onDrop={(e) => { e.preventDefault(); if (draggingId && draggingId !== u.user_id) reassign(draggingId, u.user_id); setDraggingId(null); setHoverParentId(null); }}
                      className={`p-3 rounded-xl border-2 cursor-grab active:cursor-grabbing transition-all ${isDropTarget ? "border-orange-400 bg-orange-50 shadow-md" : "border-slate-100 bg-white hover:border-orange-200"}`}
                      data-testid={`org-user-${u.user_id}`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${ROLE_COLORS[u.role] || "bg-indigo-50 text-indigo-700"}`}>{u.name?.charAt(0).toUpperCase()}</div>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-sm truncate">{u.name}</div>
                          <div className="text-[10px] text-slate-400 truncate">{u.email}</div>
                        </div>
                      </div>
                      <div className="mb-1"><RoleBadge role={u.role} customRoles={customRoles} size="xs" /></div>
                      <div className="text-[11px] text-slate-500 mt-1">
                        Reports to: <span className="font-semibold">{parent ? `${parent.name} (${ROLE_LABELS[parent.role] || parent.role})` : "—"}</span>
                      </div>
                      <select
                        value={u.parent_id || ""}
                        onChange={(e) => reassign(u.user_id, e.target.value)}
                        className="mt-2 text-xs w-full px-2 py-1 rounded border border-slate-200 bg-slate-50"
                        data-testid={`org-reports-to-${u.user_id}`}
                      >
                        <option value="">No parent (top-level)</option>
                        {users.filter((p) => p.user_id !== u.user_id).map((p) => (
                          <option key={p.user_id} value={p.user_id}>{p.name} — {ROLE_LABELS[p.role] || p.role}</option>
                        ))}
                      </select>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


function GoogleWorkspaceCard() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(null);
  const [migrate, setMigrate] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/google/status");
      setStatus(r.data);
    } catch (e) {
      setStatus({ connected: false, error: e?.response?.data?.detail || e.message });
    }
  };

  useEffect(() => {
    load();
    // Listen for popup → window.opener.postMessage('gws_connected') from our callback page
    const onMsg = (ev) => {
      if (ev?.data?.type === "gws_connected") {
        toast.success(`Connected as ${ev.data.email || "Google account"}`);
        setTimeout(load, 500);
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);

  const connect = async () => {
    setBusy("connect");
    try {
      const r = await api.post("/google/connect");
      const url = r.data?.authorize_url;
      if (!url) throw new Error("No authorize URL returned");
      // Open Google consent in a popup so the dashboard stays mounted.
      const w = window.open(url, "gws_oauth", "width=520,height=720,menubar=no,toolbar=no");
      if (!w) {
        // Pop-up blocker — fall back to redirecting current tab.
        window.location.href = url;
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Connect failed");
    } finally {
      setBusy(null);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Google? Existing migrated docs stay in your Drive — only the API link is removed.")) return;
    setBusy("disconnect");
    try {
      await api.post("/google/disconnect");
      toast.success("Disconnected");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Disconnect failed");
    } finally {
      setBusy(null);
    }
  };

  const migrateAll = async () => {
    if (!window.confirm("Migrate every legacy HTML document + LOA into Google Docs? Existing data stays — we just attach a Google Doc link to each. Safe to re-run.")) return;
    setBusy("migrate");
    setMigrate(null);
    try {
      const r = await api.post("/admin/google/migrate", {}, { timeout: 600000 });
      setMigrate(r.data);
      const d = r.data?.documents || {};
      const l = r.data?.loas || {};
      toast.success(`Migrated · ${d.migrated || 0} docs · ${l.migrated || 0} LOAs (errors ${(d.errors || 0) + (l.errors || 0)})`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Migration failed");
    } finally {
      setBusy(null);
    }
  };

  const connected = status?.connected;

  return (
    <div className="dashboard-card p-6 border-sky-200 bg-sky-50/40" data-testid="gws-card">
      <div className="flex items-start justify-between mb-2 gap-3 flex-wrap">
        <div>
          <h3 className="font-display font-bold text-lg flex items-center gap-2 text-sky-800">
            <Globe className="w-5 h-5" /> Google Workspace
          </h3>
          <p className="text-sm text-sky-900/80 mt-1 max-w-2xl">
            Connect your Google account once. From then on, every new Document, LOA, and Meeting is created as a real Google Doc / Calendar Meet under your account — full editing, comments, collaboration, version history, the works.
          </p>
        </div>
        <div className="text-xs text-right">
          {connected ? (
            <div className="space-y-1">
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800 font-semibold"><span className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></span> Connected</div>
              <div className="text-slate-600">as <b>{status?.email}</b></div>
            </div>
          ) : (
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 font-semibold"><span className="w-1.5 h-1.5 bg-slate-400 rounded-full"></span> Not connected</div>
          )}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {!connected ? (
          <button onClick={connect} disabled={!!busy} className="btn-primary py-3 px-5 flex items-center gap-2 disabled:opacity-50" data-testid="gws-connect-btn">
            {busy === "connect" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Globe className="w-4 h-4" />}
            Connect Google Account
          </button>
        ) : (
          <>
            <button onClick={migrateAll} disabled={!!busy} className="py-3 px-5 rounded-full font-semibold bg-violet-500 text-white hover:bg-violet-600 disabled:opacity-50 flex items-center gap-2" data-testid="gws-migrate-btn">
              {busy === "migrate" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              Migrate existing docs &amp; LOAs to Google Docs
            </button>
            <button onClick={disconnect} disabled={!!busy} className="btn-ghost py-3 px-5 bg-white disabled:opacity-50" data-testid="gws-disconnect-btn">Disconnect</button>
          </>
        )}
      </div>

      {migrate && (
        <div className="mt-4 grid grid-cols-2 gap-3" data-testid="gws-migrate-report">
          <div className="bg-white rounded-lg p-3 border border-slate-200">
            <div className="text-xs text-slate-500 uppercase tracking-wide">Documents</div>
            <div className="text-sm mt-1">
              <span className="font-bold text-emerald-700">{migrate.documents?.migrated || 0}</span> migrated · {migrate.documents?.skipped || 0} skipped · <span className="text-rose-600">{migrate.documents?.errors || 0}</span> errors
            </div>
          </div>
          <div className="bg-white rounded-lg p-3 border border-slate-200">
            <div className="text-xs text-slate-500 uppercase tracking-wide">LOAs</div>
            <div className="text-sm mt-1">
              <span className="font-bold text-emerald-700">{migrate.loas?.migrated || 0}</span> migrated · {migrate.loas?.skipped || 0} skipped · <span className="text-rose-600">{migrate.loas?.errors || 0}</span> errors
            </div>
          </div>
        </div>
      )}

      <p className="text-[11px] text-sky-900/60 mt-4 leading-relaxed">
        <b>How it works:</b> You stay signed in to Google in your browser. When someone clicks <i>Open</i> on a document inside MUNner's, a new tab loads the real Google Docs UI (they're a shared editor on the file). All docs live in a "MUNner's Docs" folder in your Drive — you can audit / move / archive them anytime from <code>drive.google.com</code>.
      </p>
    </div>
  );
}


function DatabaseBackupCard() {
  const [busy, setBusy] = useState(null); // 'json' | 'zip' | 'restore' | null
  const [last, setLast] = useState(null);
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [restoreFile, setRestoreFile] = useState(null);
  const [restoreMode, setRestoreMode] = useState("merge");
  const [restoreConfirm, setRestoreConfirm] = useState("");
  const [restoreSkipUsers, setRestoreSkipUsers] = useState(true);
  const [restoreReport, setRestoreReport] = useState(null);

  const download = async (fmt) => {
    if (busy) return;
    setBusy(fmt);
    try {
      const r = await api.get(`/admin/backup?format=${fmt}`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: fmt === "zip" ? "application/zip" : "application/json" });
      const disp = r.headers["content-disposition"] || "";
      const m = /filename="?([^";]+)"?/i.exec(disp);
      const fname = m?.[1] || `munners-backup.${fmt}`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = fname;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
      const cols = r.headers["x-backup-collections"];
      const docs = r.headers["x-backup-documents"];
      setLast({ fmt, fname, cols, docs, at: new Date() });
      toast.success(`Backup downloaded · ${docs} docs across ${cols} collections`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Backup failed");
    } finally {
      setBusy(null);
    }
  };

  const runRestore = async () => {
    if (!restoreFile) { toast.error("Pick a backup file first"); return; }
    if (restoreConfirm !== "RESTORE NOW") { toast.error('Type "RESTORE NOW" exactly to confirm'); return; }
    setBusy("restore");
    setRestoreReport(null);
    try {
      const fd = new FormData();
      fd.append("file", restoreFile);
      fd.append("mode", restoreMode);
      fd.append("confirm", restoreConfirm);
      fd.append("skip_users", String(restoreSkipUsers));
      const start = await api.post("/admin/restore", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const jobId = start.data?.job_id;
      if (!jobId) throw new Error("No job id returned");
      toast.success(`Restore started · ${start.data.input_docs} docs across ${start.data.input_collections} collections`);
      // Poll status every 2s
      let final = null;
      for (let i = 0; i < 600; i++) { // up to 20 min
        await new Promise(r => setTimeout(r, 2000));
        try {
          const s = await api.get(`/admin/restore/status/${jobId}`);
          setRestoreReport(s.data);
          if (s.data.status === "complete" || s.data.status === "failed") {
            final = s.data;
            break;
          }
        } catch (_) { /* ignore transient */ }
      }
      if (final?.status === "complete") {
        const t = final.totals || {};
        toast.success(`Restore complete · ${t.inserted || 0} inserted · ${t.upserted || 0} upserted · ${t.skipped || 0} skipped · ${t.errors || 0} errors`);
      } else if (final?.status === "failed") {
        toast.error(`Restore failed: ${final.error || "unknown"}`);
      } else {
        toast.error("Restore is taking longer than expected — check the report panel below");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Restore failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="dashboard-card p-6 border-emerald-200 bg-emerald-50/40" data-testid="db-backup-card">
      <div className="flex items-start justify-between mb-2 gap-3">
        <div>
          <h3 className="font-display font-bold text-lg flex items-center gap-2 text-emerald-800">
            <Database className="w-5 h-5" /> Full Database Backup
          </h3>
          <p className="text-sm text-emerald-900/80 mt-1">
            Snapshots <b>every collection</b> in the currently-connected database — users, events, committees, allotments,
            budgets, posts, blogs, tasks, documents, LOAs, meetings, custom roles, indexes, the lot. Use this <b>before</b> migrating to a new MongoDB / Firestore cluster, and use <b>Import</b> below to replay a snapshot into the active DB after migration.
          </p>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 mt-3">
        <button onClick={() => download("json")} disabled={!!busy} className="btn-primary py-3 disabled:opacity-50 flex items-center justify-center gap-2" data-testid="backup-json-btn">
          {busy === "json" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          {busy === "json" ? "Preparing…" : "Download JSON"}
        </button>
        <button onClick={() => download("zip")} disabled={!!busy} className="btn-ghost py-3 bg-white disabled:opacity-50 flex items-center justify-center gap-2" data-testid="backup-zip-btn">
          {busy === "zip" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileArchive className="w-4 h-4" />}
          {busy === "zip" ? "Preparing…" : "Download ZIP"}
        </button>
        <button onClick={() => setRestoreOpen(true)} disabled={!!busy} className="py-3 rounded-full font-semibold bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50 flex items-center justify-center gap-2" data-testid="backup-restore-btn">
          <Upload className="w-4 h-4" /> Import Backup
        </button>
      </div>
      {last && (
        <div className="mt-3 text-xs text-emerald-800 bg-white/60 rounded-lg p-2 border border-emerald-200" data-testid="backup-last">
          <b>Last backup:</b> {last.fname} · {last.docs} docs · {last.cols} collections · {last.at.toLocaleString()}
        </div>
      )}
      <p className="text-[11px] text-emerald-900/60 mt-3">
        Tip: keep the downloaded file somewhere safe (Google Drive, S3, local). Both formats are losslessly importable via the Import button above or external <code>mongoimport</code>.
      </p>

      {restoreOpen && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => !busy && setRestoreOpen(false)}>
          <div className="bg-white rounded-2xl max-w-2xl w-full p-6 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="restore-modal">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="font-display font-bold text-xl flex items-center gap-2 text-amber-700"><Upload className="w-5 h-5" /> Import / Restore Backup</h3>
                <p className="text-sm text-slate-600 mt-1">Replays a previously-downloaded <code>.json</code> or <code>.zip</code> backup into the <b>currently-connected</b> database.</p>
              </div>
              <button onClick={() => !busy && setRestoreOpen(false)} className="p-1 hover:bg-slate-100 rounded-full" data-testid="restore-close-btn"><X className="w-5 h-5" /></button>
            </div>

            <div className="space-y-4 mt-2">
              <div>
                <label className="text-sm font-semibold text-slate-700">Backup file (.json or .zip)</label>
                <input type="file" accept=".json,.zip,application/json,application/zip" onChange={(e) => setRestoreFile(e.target.files?.[0] || null)} className="block w-full mt-1 text-sm" data-testid="restore-file-input" />
                {restoreFile && <p className="text-xs text-slate-500 mt-1">{restoreFile.name} · {(restoreFile.size / 1024 / 1024).toFixed(2)} MB</p>}
              </div>

              <div>
                <label className="text-sm font-semibold text-slate-700">Import mode</label>
                <div className="mt-2 space-y-2">
                  {[
                    { v: "merge", title: "Merge / Upsert by _id (recommended)", body: "Existing docs with the same _id are OVERWRITTEN by the backup version. New docs are inserted. No collections are dropped." },
                    { v: "skip_existing", title: "Skip existing", body: "Only insert docs whose _id doesn't already exist. Safest — touches nothing already in the DB." },
                    { v: "replace", title: "Replace (DROP each collection first)", body: "DROPS every collection in the backup, then bulk-inserts. Destroys data not present in the backup. The users collection is preserved unless you uncheck the safety toggle." },
                  ].map((o) => (
                    <label key={o.v} className={`flex items-start gap-2 p-3 rounded-lg border cursor-pointer ${restoreMode === o.v ? "border-amber-500 bg-amber-50" : "border-slate-200 hover:bg-slate-50"}`} data-testid={`restore-mode-${o.v}`}>
                      <input type="radio" name="restore-mode" value={o.v} checked={restoreMode === o.v} onChange={(e) => setRestoreMode(e.target.value)} className="mt-1" />
                      <span className="text-sm">
                        <b>{o.title}</b>
                        <span className="block text-slate-600 mt-0.5 text-xs">{o.body}</span>
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {restoreMode === "replace" && (
                <label className="flex items-center gap-2 text-sm bg-rose-50 border border-rose-200 rounded-lg p-3" data-testid="restore-skip-users">
                  <input type="checkbox" checked={restoreSkipUsers} onChange={(e) => setRestoreSkipUsers(e.target.checked)} />
                  <span><b>Preserve users collection</b> (skip wiping it). Strongly recommended — uncheck only if you really want to overwrite admin accounts from the backup.</span>
                </label>
              )}

              <div>
                <label className="text-sm font-semibold text-slate-700">Type <code className="bg-slate-100 px-1 py-0.5 rounded">RESTORE NOW</code> to confirm</label>
                <input value={restoreConfirm} onChange={(e) => setRestoreConfirm(e.target.value)} placeholder='RESTORE NOW' className="input-pill mt-1" data-testid="restore-confirm-input" />
              </div>

              {restoreReport && (
                <div className="bg-slate-50 rounded-lg p-3 text-xs border border-slate-200 max-h-72 overflow-y-auto" data-testid="restore-report">
                  <div className="font-semibold text-slate-800 mb-1 flex items-center gap-2">
                    <span className={`inline-block w-2 h-2 rounded-full ${restoreReport.status === "complete" ? "bg-emerald-500" : restoreReport.status === "failed" ? "bg-rose-500" : "bg-amber-500 animate-pulse"}`}></span>
                    Restore <b>{restoreReport.status || "running"}</b> · mode={restoreReport.mode || restoreMode}
                    {restoreReport.current && <span className="text-slate-500">· current: <code>{restoreReport.current}</code></span>}
                  </div>
                  {typeof restoreReport.total_docs === "number" && restoreReport.total_docs > 0 && (
                    <div className="mb-2">
                      <div className="text-[10px] text-slate-500 mb-1">{restoreReport.processed_docs || 0} / {restoreReport.total_docs} docs</div>
                      <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
                        <div className="bg-emerald-500 h-full transition-all" style={{ width: `${Math.min(100, ((restoreReport.processed_docs || 0) / restoreReport.total_docs) * 100)}%` }}></div>
                      </div>
                    </div>
                  )}
                  <div className="grid grid-cols-4 gap-2 mb-2">
                    <Stat label="Inserted" v={restoreReport.totals?.inserted} ok />
                    <Stat label="Upserted" v={restoreReport.totals?.upserted} ok />
                    <Stat label="Skipped" v={restoreReport.totals?.skipped} />
                    <Stat label="Errors" v={restoreReport.totals?.errors} bad />
                  </div>
                  <details>
                    <summary className="cursor-pointer text-slate-600 hover:text-slate-900">Per-collection breakdown</summary>
                    <pre className="text-[10px] mt-2 whitespace-pre-wrap">{JSON.stringify(restoreReport.collections, null, 2)}</pre>
                  </details>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2 border-t">
                <button onClick={() => !busy && setRestoreOpen(false)} className="btn-ghost px-5 py-2" data-testid="restore-cancel-btn">Close</button>
                <button onClick={runRestore} disabled={!!busy || !restoreFile || restoreConfirm !== "RESTORE NOW"} className="px-5 py-2 rounded-full font-semibold bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50 flex items-center gap-2" data-testid="restore-run-btn">
                  {busy === "restore" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  {busy === "restore" ? "Restoring…" : "Run Restore"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, v, ok, bad }) {
  const color = bad && v ? "text-rose-600" : ok && v ? "text-emerald-700" : "text-slate-700";
  return (
    <div className="bg-white rounded-lg p-2 border border-slate-200">
      <div className="text-[10px] text-slate-500 uppercase tracking-wide">{label}</div>
      <div className={`text-base font-bold ${color}`}>{v ?? 0}</div>
    </div>
  );
}


function formatBytes(b) {
  if (!b && b !== 0) return "—";
  const u = ["B", "KB", "MB", "GB", "TB"];
  let i = 0, v = b;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v < 10 ? 2 : 1)} ${u[i]}`;
}

function StorageStatsCard() {
  const [s, setS] = useState(null);
  const [err, setErr] = useState(null);
  const [open, setOpen] = useState(false);
  const load = async () => {
    setErr(null);
    try { const r = await api.get("/admin/storage-stats"); setS(r.data); }
    catch (e) { setErr(e?.response?.data?.detail || e.message || "Unable to read storage stats"); }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);
  if (err) {
    return (
      <div className="dashboard-card p-6" data-testid="storage-card">
        <h3 className="font-display font-bold text-lg mb-2 flex items-center gap-2"><Database className="w-5 h-5 text-emerald-600" /> Database Storage</h3>
        <div className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-xs text-rose-700 mb-2"><b>Error:</b> {err}</div>
        <button onClick={load} className="btn-ghost text-xs" data-testid="storage-retry"><RefreshCw className="w-3 h-3" /> Retry</button>
      </div>
    );
  }
  if (!s) return <div className="dashboard-card p-6 text-sm text-slate-400">Loading storage…</div>;
  const usedPct = s.quota_used_pct != null ? Math.min(100, s.quota_used_pct) : (s.fs_total_bytes ? Math.min(100, (s.fs_used_bytes / s.fs_total_bytes) * 100) : null);
  const setQuota = async () => {
    const cur = s.quota_label || "";
    const ans = window.prompt(
      "Set a SOFT BUDGET alarm in GB.\n\nThis isn't your actual database limit — Firestore Enterprise is pay-as-you-go (no fixed quota). This number is just a target so we can warn you when usage crosses 80% / 90%.\n\nExamples: 5, 10, 25, 100  (in GB)\n\nLeave blank and OK to CLEAR the budget.",
      cur ? String(s.quota_bytes / (1024**3)) : "10"
    );
    if (ans === null) return; // cancelled
    try {
      if (ans.trim() === "") {
        await api.post("/admin/storage-quota", { quota_bytes: null });
        toast.success("Quota cleared");
      } else {
        const gb = parseFloat(ans);
        if (!isFinite(gb) || gb <= 0) return toast.error("Enter a positive number of GB");
        await api.post("/admin/storage-quota", { gb });
        toast.success(`Quota set to ${gb} GB`);
      }
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to set quota"); }
  };
  return (
    <div className="dashboard-card p-6" data-testid="storage-card">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="font-display font-bold text-lg flex items-center gap-2"><Database className="w-5 h-5 text-emerald-600" /> Database Storage</h3>
          {s.sampled && <div className="text-[10px] text-slate-400 mt-0.5">Live usage — sampled directly from {s.db_name}</div>}
        </div>
        <button onClick={setQuota} className="btn-ghost text-xs py-1.5 px-2" data-testid="set-quota-btn" title="Set a soft-budget alarm threshold (not your actual DB limit)">
          <Settings className="w-3 h-3" /> {s.quota_bytes ? "Edit budget" : "Set budget"}
        </button>
      </div>

      {/* Quota bar (the headline KPI) */}
      {s.quota_bytes ? (
        <div className="mb-4 p-4 rounded-2xl bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-100" data-testid="quota-panel">
          <div className="flex items-end justify-between mb-2">
            <div>
              <div className="text-[10px] uppercase tracking-wide font-bold text-emerald-700">Disk usage</div>
              <div className="font-display font-black text-2xl text-slate-900" data-testid="quota-used">
                {formatBytes(s.total_used_bytes)} <span className="text-slate-400 font-normal text-base">of {s.quota_label}</span>
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wide font-bold text-slate-400">Free</div>
              <div className="font-display font-black text-xl text-emerald-600" data-testid="quota-free">{formatBytes(s.quota_free_bytes)}</div>
            </div>
          </div>
          <div className="w-full bg-white/60 rounded-full h-3 overflow-hidden shadow-inner">
            <div
              className={`h-3 rounded-full transition-all ${usedPct > 90 ? "bg-rose-500" : usedPct > 70 ? "bg-amber-500" : "bg-emerald-500"}`}
              style={{ width: `${usedPct}%` }}
              data-testid="quota-progress"
            />
          </div>
          <div className="text-xs font-bold text-slate-500 mt-1.5 text-right">{usedPct.toFixed(2)}% used</div>
        </div>
      ) : (
        <div className="mb-4 p-4 rounded-2xl bg-amber-50 border border-amber-200" data-testid="quota-empty">
          <div className="text-sm font-semibold text-amber-900">Soft budget not set</div>
          <div className="text-xs text-amber-700 mt-1">Click <b>Set budget</b> above and enter a target GB amount (e.g., <code>10</code>) — we&apos;ll warn you when usage crosses 80%. Firestore Enterprise itself has no fixed quota (pay-as-you-go).</div>
        </div>
      )}

      <div className="space-y-2 text-sm">
        <div className="flex items-center justify-between"><span className="text-slate-500">Database</span><span className="font-semibold">{s.db_name}</span></div>
        <div className="flex items-center justify-between"><span className="text-slate-500">Collections</span><span className="font-semibold" data-testid="storage-collections">{s.total_collections}</span></div>
        <div className="flex items-center justify-between"><span className="text-slate-500">Documents</span><span className="font-semibold" data-testid="storage-documents">{(s.total_objects || 0).toLocaleString()}</span></div>
        <div className="flex items-center justify-between"><span className="text-slate-500">Data size</span><span className="font-semibold" data-testid="storage-data-size">{formatBytes(s.data_size_bytes)}</span></div>
        <div className="flex items-center justify-between">
          <span className="text-slate-500">Indexes ({s.indexes})</span>
          <span className="font-semibold">
            {formatBytes(s.index_size_bytes)}
            {s.index_size_estimated && <span className="ml-1 text-[10px] text-amber-600" title="Index size isn't exposed by Firestore/Cosmos. Shown as ~20% of data size — typical Mongo ratio.">*est</span>}
          </span>
        </div>
        <div className="flex items-center justify-between"><span className="text-slate-500">Storage on disk</span><span className="font-semibold">{formatBytes(s.storage_size_bytes)}</span></div>
      </div>
      <button onClick={() => setOpen((v) => !v)} className="mt-4 text-xs text-orange-600 hover:underline" data-testid="storage-toggle-cols">
        {open ? "Hide" : "View"} collections breakdown
      </button>
      {open && (
        <div className="mt-3 border-t border-slate-100 pt-3 max-h-64 overflow-y-auto">
          <table className="w-full text-xs">
            <thead><tr className="text-left text-slate-400"><th className="font-medium pb-1">Collection</th><th className="text-right">Docs</th><th className="text-right">Size</th></tr></thead>
            <tbody>
              {s.collections.map((c) => (
                <tr key={c.name} className="border-t border-slate-50">
                  <td className="py-1 font-mono text-slate-700">{c.name}</td>
                  <td className="py-1 text-right">{c.count}</td>
                  <td className="py-1 text-right">{formatBytes(c.size_bytes)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function PingsAndResolverCard() {
  const [pings, setPings] = useState([]);
  const [resolverLog, setResolverLog] = useState([]);
  const [onlyErrors, setOnlyErrors] = useState(false);
  const [running, setRunning] = useState(false);

  const load = async () => {
    try {
      const [p, r] = await Promise.all([
        api.get("/admin/pings", { params: { limit: 60, only_errors: onlyErrors } }),
        api.get("/admin/resolver/log", { params: { limit: 8 } }),
      ]);
      setPings(p.data); setResolverLog(r.data);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, [onlyErrors]);

  const runResolver = async () => {
    setRunning(true);
    try {
      const r = await api.post("/admin/resolver/run");
      toast.success(`Resolver applied ${r.data.fixes.length} fix(es)`);
      load();
    } catch { toast.error("Resolver failed"); } finally { setRunning(false); }
  };

  const clearPings = async () => {
    if (!window.confirm("Clear all ping records?")) return;
    await api.delete("/admin/pings");
    load();
  };

  return (
    <div className="dashboard-card p-6" data-testid="pings-resolver-card">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display font-bold text-lg flex items-center gap-2"><Activity className="w-5 h-5 text-orange-600" /> Pings & Auto-resolver</h3>
        <div className="flex items-center gap-2">
          <label className="text-xs flex items-center gap-1 cursor-pointer">
            <input type="checkbox" checked={onlyErrors} onChange={(e) => setOnlyErrors(e.target.checked)} data-testid="pings-only-errors" /> Errors only
          </label>
          <button onClick={runResolver} disabled={running} className="btn-ghost text-xs py-1.5" data-testid="resolver-run-btn">{running ? "Running…" : "Run resolver"}</button>
        </div>
      </div>

      {/* Recent pings */}
      <div className="max-h-56 overflow-y-auto border border-slate-100 rounded-xl">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 sticky top-0">
            <tr className="text-left text-slate-500">
              <th className="px-2 py-1.5 font-medium">Time</th>
              <th className="px-2 py-1.5 font-medium">Method</th>
              <th className="px-2 py-1.5 font-medium">Path</th>
              <th className="px-2 py-1.5 font-medium text-right">Status</th>
              <th className="px-2 py-1.5 font-medium text-right">ms</th>
            </tr>
          </thead>
          <tbody>
            {pings.length === 0 && <tr><td colSpan="5" className="text-center text-slate-400 py-4">No pings recorded.</td></tr>}
            {pings.map((p) => (
              <tr key={p.ping_id} className="border-t border-slate-50">
                <td className="px-2 py-1 text-slate-500 whitespace-nowrap">{new Date(p.at).toLocaleTimeString()}</td>
                <td className="px-2 py-1 font-mono text-[10px] text-slate-600">{p.method}</td>
                <td className="px-2 py-1 font-mono text-[10px] truncate max-w-[180px]">{p.path}</td>
                <td className={`px-2 py-1 text-right font-semibold ${p.status >= 500 ? "text-rose-600" : p.status >= 400 ? "text-amber-600" : "text-emerald-600"}`}>{p.status}</td>
                <td className="px-2 py-1 text-right text-slate-500">{p.latency_ms}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex justify-between items-center mt-2 text-xs text-slate-400">
        <span>{pings.length} recent pings · auto-resolver runs every 5 min</span>
        <button onClick={clearPings} className="hover:text-rose-500" data-testid="pings-clear-btn">Clear log</button>
      </div>

      {/* Resolver log */}
      {resolverLog.length > 0 && (
        <div className="mt-4 pt-3 border-t border-slate-100">
          <div className="text-xs font-semibold text-slate-500 mb-2">Resolver activity (recent)</div>
          <div className="space-y-2 max-h-40 overflow-y-auto" data-testid="resolver-log">
            {resolverLog.map((r, i) => (
              <div key={i} className="text-xs bg-emerald-50 border border-emerald-100 rounded-lg p-2">
                <div className="font-semibold text-emerald-700">{new Date(r.at).toLocaleString()} {r.forced && <span className="text-[10px] text-amber-600">(manual)</span>}</div>
                <ul className="ml-3 mt-1 list-disc text-emerald-700/90">
                  {(r.fixes || []).map((f, j) => <li key={j}>{f}</li>)}
                </ul>
                {r.error && <div className="text-rose-600 mt-1">⚠ {r.error}</div>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


function SystemRepairButton() {
  const [running, setRunning] = useState(false);
  const [report, setReport] = useState(null);
  const run = async () => {
    if (!window.confirm("Run a deep System Repair?\n\nThis is SAFE — admin's edits and deletes are preserved.\n• Ensures the platform admin always exists\n• Recreates database indexes\n• Cleans up legacy roles\n• Re-loads SMTP from DB\n• Surfaces orphaned data warnings\n\nDeleted users are NOT re-created — use the 'Seed demo' button if you need demo accounts back.")) return;
    setRunning(true);
    try {
      const r = await api.post("/admin/system-repair");
      setReport(r.data.fixes);
      toast.success(`System Repair complete · ${r.data.fixes.length} actions`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Repair failed");
    } finally { setRunning(false); }
  };
  return (
    <>
      <button onClick={run} disabled={running} className="mt-3 w-full bg-emerald-600 text-white rounded-full py-3 font-bold hover:bg-emerald-700 disabled:opacity-50" data-testid="system-repair-btn">
        <RefreshCw className="w-4 h-4 inline mr-2" /> {running ? "Repairing…" : "Run System Repair (safe)"}
      </button>
      {report && (
        <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded-xl max-h-48 overflow-y-auto text-xs" data-testid="system-repair-report">
          <div className="font-bold text-emerald-700 mb-1">Repair report</div>
          <ul className="list-disc ml-4 text-emerald-700/90 space-y-0.5">
            {report.map((r, i) => <li key={i}>{r}</li>)}
          </ul>
        </div>
      )}
    </>
  );
}

function CityManager() {
  const [cities, setCities] = useState([]);
  const [adding, setAdding] = useState("");
  const [filter, setFilter] = useState("");
  const load = async () => { const r = await api.get("/cities"); setCities(r.data); };
  useEffect(() => { load(); }, []);
  const add = async (e) => {
    e.preventDefault();
    if (!adding.trim()) return;
    try {
      await api.post("/cities", { name: adding.trim() });
      toast.success(`City "${adding.trim()}" added`);
      setAdding("");
      load();
    } catch (e2) { toast.error(e2?.response?.data?.detail || "Failed"); }
  };
  const del = async (name) => {
    if (!window.confirm(`Remove "${name}" from the cities list? (Existing users keep this city.)`)) return;
    try {
      await api.delete(`/cities/${encodeURIComponent(name)}`);
      toast.success("Removed");
      load();
    } catch (e2) { toast.error("Built-in cities cannot be removed"); }
  };
  const filtered = filter ? cities.filter((c) => c.toLowerCase().includes(filter.toLowerCase())) : cities;
  return (
    <div className="dashboard-card p-6" data-testid="city-manager">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h3 className="font-display font-bold text-lg flex items-center gap-2"><MapPin className="w-5 h-5 text-orange-600" /> Cities ({cities.length})</h3>
        <form onSubmit={add} className="flex gap-2">
          <input value={adding} onChange={(e) => setAdding(e.target.value)} placeholder="Add a new city…" className="input-pill text-sm py-2" data-testid="city-add-input" />
          <button type="submit" className="btn-primary py-2 px-4 text-sm" data-testid="city-add-btn">+ Add</button>
        </form>
      </div>
      <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter cities…" className="input-pill text-sm py-2 mb-3" />
      <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto">
        {filtered.map((c) => (
          <span key={c} className="inline-flex items-center gap-1 bg-orange-50 text-orange-700 rounded-full px-3 py-1 text-xs">
            {c}
            <button onClick={() => del(c)} className="text-rose-400 hover:text-rose-600 ml-1" title="Remove">×</button>
          </span>
        ))}
        {filtered.length === 0 && <div className="text-sm text-slate-400">No matches.</div>}
      </div>
      <p className="text-[11px] text-slate-400 mt-2">Built-in cities can't be removed via this list, but custom ones can. New cities you add here appear in every dropdown across MUNner's.</p>
    </div>
  );
}

